import { TFunction, useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { getClientTimezone } from '@dxb-web-ui-framework/common/utils/Date';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import { AccountExtraitTransactionListFilters } from '../../../../account/src/contexts/AccountDetails/types';
import { transactionTypeToChipTitleTransformer } from '../../../../content-composer/src/utils';
import {
  dateRangeFormatter,
  amountFormatter,
} from '../../../../content-composer/src/components/Generic/SelectedFiltersChip/ValueFormatters/ValueFormatters';

const MIN_AMOUNT = 0;
const MAX_AMOUNT = 9999999999.99;
export const TRANSACTION_TYPE_DEFAULT_VALUE = 0;

const schemaAmount = (type: string, t: TFunction) =>
  yup
    .mixed()
    .test(
      'number',
      t('AccountExtraitTransactionsFilter.Validation.Range', { min: MIN_AMOUNT, max: MAX_AMOUNT }),
      value => (value ? !isNaN(value) && Number(value) > 0 && Number(value) < MAX_AMOUNT : true),
    )
    .test('validFormat', t('AccountExtraitTransactionsFilter.Validation.UpTwoDecimals'), value =>
      /^\d+(\.\d+)|\s?$/.test(value),
    )
    .when(type, {
      is: (value: string) => !!value,
      then: yup.string().required(t('AccountExtraitTransactionsFilter.Validation.Required')),
      otherwise: yup.string(),
    });

const useAccountExtraitTransactionsFiltersConfig = (
  filters: Nullable<Partial<AccountExtraitTransactionListFilters>>,
  selectedAccountCurrency?: string,
): FormConfig => {
  const { t } = useTranslation();
  const { cultureName } = useLanguage();
  const timezone = getClientTimezone();
  const dateFormat = t('global.settings.date.format');

  return {
    validationSchema: yup.object().shape(
      {
        fromAmount: schemaAmount('toAmount', t),
        toAmount: schemaAmount('fromAmount', t),
      },
      [['toAmount', 'fromAmount']],
    ),
    components: [
      {
        key: 'transactionDate',
        type: 'datePickerRange',
        name: 'transactionDate',
        label: t('accountExtraitTransactionsFiltersDrawer.DateRangePicker.Label'),
        properties: {
          formatExp: t('global.settings.date.format'),
          localeName: cultureName,
          tz: timezone,
          mt: 3,
          btnLabel: t('accountExtraitTransactionsFiltersDrawer.DateRangePicker.btnLabel'),
          filters: {
            rangeReference: {
              from: 'dateFrom',
              to: 'dateTo',
            },
            valueFormatter: (dateRange: { from: string; to: string }) =>
              dateRangeFormatter(dateRange.from, dateRange.to, dateFormat),
          },
        },
      },
      {
        key: 'transactionType',
        type: 'select',
        name: 'transactionType',
        label: t('accountExtraitTransactionsFiltersDrawer.Type.Label'),
        options: [
          { value: 1, label: t('Filters.Debit') },
          { value: 2, label: t('Filters.Credit') },
        ],
        emptyOption: {
          value: TRANSACTION_TYPE_DEFAULT_VALUE,
          label: t('useAccountExtraitTransactionsFiltersConfig.EmptyOption.Label'),
        },
        properties: {
          variant: 'filled',
          filters: {
            valueFormatter: (transactionType: number) =>
              transactionType === TRANSACTION_TYPE_DEFAULT_VALUE
                ? t('useAccountExtraitTransactionsFiltersConfig.EmptyOption.Label')
                : transactionTypeToChipTitleTransformer(t, transactionType),
          },
        },
      },
      {
        type: 'fieldSet',
        key: 'fieldSet',
        label: 'Amount',
        components: [
          {
            key: 'fromAmount',
            type: 'floatNumber',
            name: 'fromAmount',
            label: t('accountExtraitTransactionsFiltersDrawer.AmountFrom.Label'),
            properties: {
              variant: 'filled',
              allowNegative: true,
              mr: 2,
            },
          },
          {
            key: 'toAmount',
            type: 'floatNumber',
            name: 'toAmount',
            label: t('accountExtraitTransactionsFiltersDrawer.AmountTo.Label'),
            properties: {
              variant: 'filled',
              allowNegative: true,
            },
          },
        ],
        properties: {
          isRow: true,
          shouldGroupValidation: false,
          filters: {
            rangeReference: {
              from: 'fromAmount',
              to: 'toAmount',
            },
            valueFormatter: (amountRange: { from: number; to: number }) =>
              `${amountFormatter(amountRange.from, selectedAccountCurrency)} - ${amountFormatter(
                amountRange.to,
                selectedAccountCurrency,
              )}`,
          },
        },
      },
    ],
  };
};

export { useAccountExtraitTransactionsFiltersConfig };
